import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  Modal,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Form,
  Space,
  Tag,
} from "antd";
import S3Image from "../../../component/S3Image";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  YoutubeOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { GET, POST, PUT, DELETE } from "../../../utils/apirequest";
import { API } from "../../../config/API";
import Loadingbox from "../../../component/loadingbox";
import type { ColumnsType } from "antd/es/table";
import { useSelector } from "react-redux";
import moment from "moment";

const { TextArea } = Input;

interface Video {
  id: number;
  title: string;
  file: string; // YouTube Video ID
  is_image: boolean;
  createdAt: string;
  updatedAt: string;
  attributes?: {
    title: string;
    file: string;
    is_image: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

interface VideosTabProps {
  isActive?: boolean;
}

const VideosTab: React.FC<VideosTabProps> = ({ isActive = true }) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [form] = Form.useForm();
  const User = useSelector((state: any) => state?.Auth);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Call API when tab becomes active or pagination changes
  useEffect(() => {
    if (isActive) {
      loadVideos();
    }
  }, [isActive, currentPage, pageSize]);

  const loadVideos = async () => {
    try {
      setLoading(true);

      // Build API URL with required filters and pagination
      const params = new URLSearchParams({
        "filters[is_deleted][$eq]": "false",
        "filters[is_image][$eq]": "false",
        "filters[type][$eq]": "video",
        "sort[0]": "order:asc",
        "sort[1]": "createdAt:desc",
        "pagination[page]": currentPage.toString(),
        "pagination[pageSize]": pageSize.toString(),
      });

      const apiUrl = `${API.SPECIAL_PAGE_MEDIA_LIST}?${params.toString()}`;
      console.log("Video API URL:", apiUrl); // Debug log

      const response: any = await GET(apiUrl, null, null);
      console.log("Video API Response:", response); // Debug log

      if (response) {
        // Handle Strapi v4 response structure
        let videosData = [];
        let totalCount = 0;

        if (response.data) {
          videosData = Array.isArray(response.data)
            ? response.data
            : [response.data];

          // Extract pagination info
          if (response.meta?.pagination) {
            totalCount = response.meta.pagination.total;
            setTotal(totalCount);
          } else {
            setTotal(videosData.length);
          }
        } else if (Array.isArray(response)) {
          videosData = response;
          setTotal(videosData.length);
        } else if (response.attributes) {
          videosData = [response];
          setTotal(1);
        }

        console.log("Video data:", videosData); // Debug log
        console.log("Total count:", totalCount); // Debug log
        setVideos(videosData);
      } else {
        console.log("No response received");
        setVideos([]);
        setTotal(0);
      }
    } catch (error: any) {
      console.error("Error loading videos:", error);
      console.error("Error details:", error.response || error.message);
      message.error("Failed to load videos");
      setVideos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const getThumbnailUrl = (youtubeUrl: string): string => {
    const videoId = `https://img.youtube.com/vi/${youtubeUrl}/maxresdefault.jpg`;
    return videoId;
  };

  const getEmbedUrl = (youtubeUrl: string): string => {
    const videoId = `https://www.youtube.com/embed/${youtubeUrl}`;
    return videoId;
  };

  const handleAdd = () => {
    setEditMode(false);
    setSelectedVideo(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (video: Video) => {
    setEditMode(true);
    setSelectedVideo(video);
    const videoData: any = video.attributes || video;
    form.setFieldsValue({
      description: videoData.title,
      youtubeUrl: videoData.file,
      order: videoData.order || 0,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const videoData = {
        data: {
          title: values.description || "",
          file: values.youtubeUrl,
          is_image: false,
          is_deleted: false,
          type: "video",
          order: values.order || 0,
          caption: values.caption || "",
          created_by: User?.user?.id,
        },
      };

      let response: any;
      if (editMode && selectedVideo) {
        response = await PUT(
          `${API.SPECIAL_PAGE_MEDIA_EDIT}${selectedVideo.id}`,
          videoData
        );
      } else {
        response = await POST(API.SPECIAL_PAGE_MEDIA_ADD, videoData);
      }

      if (response?.data) {
        message.success(
          editMode ? "Video updated successfully!" : "Video added successfully!"
        );
        // Reset to first page and reload
        setCurrentPage(1);
        loadVideos();
        setModalVisible(false);
        form.resetFields();
      }
    } catch (error) {
      console.error("Error saving video:", error);
      message.error("Failed to save video");
    }
  };

  const handleDelete = async (videoId: number) => {
    try {
      const response = await DELETE(
        `${API.SPECIAL_PAGE_MEDIA_DELETE}${videoId}`
      );
      if (response) {
        message.success("Video deleted successfully!");
        // If current page has no items after deletion, go to previous page
        if (videos.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          loadVideos();
        }
      }
    } catch (error) {
      console.error("Error deleting video:", error);
      message.error("Failed to delete video");
    }
  };

  // Handle pagination changes
  const handleTableChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  };

  // Handle manual refresh
  const handleRefresh = () => {
    setCurrentPage(1);
    loadVideos();
  };

  const handlePreview = (video: Video) => {
    const videoData = video.attributes || video;
    const embedUrl = getEmbedUrl(videoData.file);
    if (embedUrl) {
      Modal.info({
        title: videoData.title || "Video Preview",
        content: (
          <iframe
            width="100%"
            height="315"
            src={embedUrl}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ),
        width: 600,
        icon: null,
      });
    }
  };

  const columns: ColumnsType<Video> = [
    {
      title: "Thumbnail",
      dataIndex: "file",
      key: "thumbnail",
      width: 100,
      render: (file: string, record: Video) => {
        const videoData = record.attributes || record;
        const thumbnailUrl = getThumbnailUrl(videoData.file);
        return (
          <div style={{ position: "relative" }}>
            <S3Image
              src={thumbnailUrl}
              alt={videoData.title}
              width={60}
              height={60}
              style={{ objectFit: "cover", borderRadius: 4 }}
              preview={false}
            />
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: 16,
                color: "white",
                textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                cursor: "pointer",
              }}
              onClick={() => handlePreview(record)}
            >
              <PlayCircleOutlined />
            </div>
          </div>
        );
      },
    },
    {
      title: "Title/Description",
      dataIndex: "title",
      key: "title",
      render: (title: string, record: Video) => {
        const videoData = record.attributes || record;
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {videoData.title || "Untitled"}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>YouTube Video</div>
          </div>
        );
      },
    },
    {
      title: "Video ID",
      dataIndex: "file",
      key: "url",
      width: 200,
      render: (file: string, record: Video) => {
        const videoData = record.attributes || record;
        return (
          <div style={{ fontSize: "12px", color: "#666" }}>
            <a
              href={videoData.file}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1890ff" }}
            >
              {videoData.file.length > 40
                ? `${videoData.file.substring(0, 40)}...`
                : videoData.file}
            </a>
          </div>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (date: string, record: Video) => {
        const photoData = record.attributes || record;
        let _date = moment(photoData.createdAt).format("DD-MM-YYYY HH:MM A");
        return _date;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record: Video) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
            size="small"
          >
            Preview
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this video?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return <Loadingbox />;
  }

  return (
    <div>
      <br />
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ color: "#666" }}>Total Videos: {total}</span>
        <div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add New Video
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            style={{ marginLeft: 8 }}
          >
            Refresh
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={videos}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} videos`,
          onChange: handleTableChange,
          onShowSizeChange: (current, size) => handleTableChange(current, size),
          pageSizeOptions: ["5", "10", "20", "50"],
        }}
        locale={{
          emptyText: "No videos found. Click 'Add New Video' to get started.",
        }}
        loading={loading}
      />

      {/* Add/Edit Video Modal */}
      <Modal
        title={editMode ? "Edit Video" : "Add New Video"}
        visible={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText="Save"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="youtubeUrl"
            label="YouTube Video ID"
            rules={[
              { required: true, message: "Please enter YouTube Video ID" },
            ]}
          >
            <Input placeholder="VtAshkhTRG0" prefix={<YoutubeOutlined />} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (max 100 characters)"
            rules={[
              {
                max: 100,
                message: "Description cannot exceed 100 characters",
              },
            ]}
          >
            <TextArea
              rows={3}
              maxLength={100}
              placeholder="Enter video description or title (max 100 characters)"
              showCount
            />
          </Form.Item>

          <Form.Item
            name="order"
            label="Order"
            rules={[{ required: true, message: "Please enter order value" }]}
          >
            <InputNumber
              min={0}
              step={0.1}
              placeholder="Enter order value (supports decimals)"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.youtubeUrl !== currentValues.youtubeUrl
            }
          >
            {({ getFieldValue }) => {
              const youtubeUrl = getFieldValue("youtubeUrl");
              if (youtubeUrl) {
                const embedUrl = getEmbedUrl(youtubeUrl);
                return (
                  <Form.Item label="Preview">
                    <div style={{ marginTop: 8 }}>
                      <iframe
                        width="100%"
                        height="200"
                        src={embedUrl}
                        title="YouTube video preview"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VideosTab;
